import { t } from '@/i18n';
import type { CounterpartyTotal } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { formatPLN } from './chart-utils';

interface TopCounterpartiesProps {
  topCustomers: CounterpartyTotal[];
  topVendors: CounterpartyTotal[];
  showCustomers: boolean;
}

function CounterpartyList({
  title,
  items,
  color,
}: {
  title: string;
  items: CounterpartyTotal[];
  color: string;
}) {
  const max = Math.max(...items.map((i) => i.total), 1);

  return (
    <ChartCard title={title}>
      {items.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          {t('dashboard.financial.noCounterparties')}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((item) => (
            <div key={`${item.nip ?? ''}-${item.name}`} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium" title={item.name}>
                    {item.name}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {item.nip ? `NIP ${item.nip} · ` : ''}
                    {t('dashboard.financial.invoicesCount', { count: item.count })}
                  </p>
                </div>
                <p className="shrink-0 font-mono text-sm font-medium tabular-nums">
                  {formatPLN(item.total)}
                  <span className="text-muted-foreground ml-1 font-sans text-xs font-normal">PLN</span>
                </p>
              </div>
              <div className="bg-muted h-1.5 overflow-hidden rounded-full" aria-hidden="true">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.max((item.total / max) * 100, 1)}%`, background: color }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

export function TopCounterparties({ topCustomers, topVendors, showCustomers }: TopCounterpartiesProps) {
  return (
    <div className={`grid gap-4 ${showCustomers ? 'xl:grid-cols-2' : ''}`}>
      {showCustomers && (
        <CounterpartyList
          title={t('dashboard.financial.topCustomers')}
          items={topCustomers}
          color="var(--chart-1)"
        />
      )}
      <CounterpartyList
        title={t('dashboard.financial.topVendors')}
        items={topVendors}
        color="var(--chart-4)"
      />
    </div>
  );
}
